import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { EmptyState } from "@/components/empty-state";
import { PageHeader } from "@/components/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ApiError } from "@/lib/api";
import {
  createResource,
  listResource,
  type StockMovementRow,
  type StockRow,
} from "@/lib/master-data";
import { formatDateTime } from "@/lib/utils";

const MOVEMENT_LABELS: Record<string, string> = {
  inbound: "入库",
  issue: "领用",
  correction: "冲正",
  reversal: "反冲",
};

export function SettingsSpecialPage() {
  const queryClient = useQueryClient();
  const [materialId, setMaterialId] = useState("");
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const stockQuery = useQuery({
    queryKey: ["consumable-stock"],
    queryFn: () => listResource<StockRow>("/api/v1/consumables/stock"),
  });
  const movementsQuery = useQuery({
    queryKey: ["stock-movements"],
    queryFn: () => listResource<StockMovementRow>("/api/v1/admin/stock-movements"),
  });
  const stock = stockQuery.data ?? [];
  const selected = stock.find((row) => row.materialId === materialId);

  async function submit() {
    if (!materialId) return toast.error("请选择耗材");
    if (!delta.trim() || Number(delta) === 0) return toast.error("调整数量不能为 0");
    if (!reason.trim()) return toast.error("请填写冲正原因");
    setBusy(true);
    try {
      await createResource("/api/v1/admin/stock-corrections", {
        materialId,
        quantityDelta: Number(delta),
        reason,
      });
      toast.success("库存冲正完成");
      setDelta("");
      setReason("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["consumable-stock"] }),
        queryClient.invalidateQueries({ queryKey: ["stock-movements"] }),
      ]);
    } catch (error) {
      toast.error(error instanceof ApiError ? error.message : "冲正失败");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="特殊情况处理"
        description="仅系统管理员可用的高风险操作；库存冲正必须填写原因并写入审计，冲正后库存不能为负数。"
        actions={
          <Button
            variant="outline"
            onClick={() => {
              stockQuery.refetch();
              movementsQuery.refetch();
            }}
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </Button>
        }
      />

      <div className="grid gap-6 xl:grid-cols-[0.8fr_1.2fr]">
        <Card>
          <CardHeader>
            <CardTitle>耗材库存冲正</CardTitle>
            <CardDescription>
              正数增加库存，负数减少库存；错误领用请优先使用业务冲正，不要直接改库。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>耗材</Label>
              <select
                value={materialId}
                onChange={(event) => setMaterialId(event.target.value)}
                className="h-10 w-full rounded-md border border-input bg-card px-3 text-sm"
              >
                <option value="">请选择耗材</option>
                {stock.map((row) => (
                  <option key={row.materialId} value={row.materialId}>
                    {row.materialCode} · {row.materialName}（结存 {row.quantity}）
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label>调整数量（正/负）</Label>
              <Input
                type="number"
                value={delta}
                onChange={(event) => setDelta(event.target.value)}
                placeholder="例如 -2"
              />
            </div>
            <div className="space-y-2">
              <Label>冲正原因</Label>
              <Input
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="必须填写，写入审计"
              />
            </div>
            {selected ? (
              <p className="text-xs text-muted-foreground">
                冲正后预计结存：{selected.quantity + Number(delta || 0)}
              </p>
            ) : null}
            <div className="flex items-center gap-2 rounded-md bg-amber-50 p-2 text-xs text-amber-800">
              <AlertTriangle className="h-4 w-4" />
              高风险操作，请确认业务依据。
            </div>
            <Button onClick={submit} disabled={busy}>
              确认冲正
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>最近库存流水</CardTitle>
            <CardDescription>包含入库、领用与冲正，按时间倒序。</CardDescription>
          </CardHeader>
          <CardContent>
            {movementsQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">加载中…</p>
            ) : (movementsQuery.data ?? []).length === 0 ? (
              <EmptyState title="暂无流水" description="耗材入库或领用后产生流水。" />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>时间</TableHead>
                    <TableHead>类型</TableHead>
                    <TableHead>数量</TableHead>
                    <TableHead>原因</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(movementsQuery.data ?? []).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="text-xs">{formatDateTime(row.occurredAt)}</TableCell>
                      <TableCell>
                        <Badge variant={row.movementType === "correction" ? "warning" : "outline"}>
                          {MOVEMENT_LABELS[row.movementType] ?? row.movementType}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {row.quantityDelta > 0 ? `+${row.quantityDelta}` : row.quantityDelta}
                      </TableCell>
                      <TableCell className="max-w-[200px] truncate">{row.reason ?? "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
